'use client';
import { context } from './account-provider';
import { meContext } from '../auth/me-provider';
import { appConfigurationContext } from './app-configuration-provider';
import { useContext, useMemo } from 'react';
import { validateAccount } from '../auth/validate-account';
import { useCookies } from 'next-client-cookies';
import {
  UserServiceRole,
  ClientAccount,
  ClientBanzaiRole,
  DefaultRoles,
  ClientService,
} from '@banzai/data/auth';
import { getHighestRole } from './get-highest-role';

export const OVERRIDE_ADMIN_COOKIE_NAME = 'bnz_disableadmin';

export interface AccountInformation {
  account: ClientAccount;
  role: UserServiceRole;
  banzaiRole?: ClientBanzaiRole;
  highestRole: DefaultRoles;
  highestRoleFromBanzai: boolean;
  otherAccountsForService: Array<ClientAccount>;
  otherServices: Array<ClientService>;
  originalBanzaiRole?: ClientBanzaiRole;
  overrideAdmin: boolean;
}

export function useAccount(): AccountInformation | undefined {
  const account = useContext(context);
  const appConfig = useContext(appConfigurationContext);
  const me = useContext(meContext);
  const cookiesStore = useCookies();

  const overrideAdmin = cookiesStore.get(OVERRIDE_ADMIN_COOKIE_NAME) === 'true';

  const accountResult = useMemo<AccountInformation | undefined>(() => {
    if (!me || !me.user) {
      return;
    }

    const banzaiRole = me.user.banzaiRoles.find(x => x.service_id === appConfig.serviceId);

    if (!account && !banzaiRole) {
      return;
    }

    if (!account) {
      const result: AccountInformation = {
        banzaiRole: overrideAdmin ? undefined : banzaiRole,
        highestRole: banzaiRole!.name as DefaultRoles,
        highestRoleFromBanzai: true,
        otherAccountsForService: [],
        otherServices: [],
        role: {
          name: banzaiRole!.name as DefaultRoles,
          service_id: appConfig.serviceId,
          service: {
            name: appConfig.appName,
            service_id: appConfig.serviceId,
            url: '/',
          },
        },
        account: undefined as any,
        originalBanzaiRole: banzaiRole,
        overrideAdmin,
      };

      return result;
    }

    const validationResult = validateAccount(appConfig.serviceId, account, me.user);

    const highestRole = getHighestRole(validationResult.role?.name, banzaiRole?.name);
    const highestRoleFromBanzai = banzaiRole?.name === highestRole;

    const otherAccountsForService = me.user.accounts
      .filter(
        x =>
          x.account.account_id !== account.account_id &&
          x.roles.find(y => y.service_id === appConfig.serviceId)
      )
      .map(x => x.account);

    const otherServicesArray = Array.from(
      new Map(
        me.user.accounts
          .flatMap(x => x.roles)
          .filter(x => x.service_id !== appConfig.serviceId && x.service_id !== 'banzai')
          .map(x => [x.service_id, x.service])
      )
    );

    const otherServices = otherServicesArray.map(x => x[1]);

    const result: AccountInformation = {
      account: validationResult.account?.account as ClientAccount,
      role: validationResult.role as UserServiceRole,
      banzaiRole: overrideAdmin ? undefined : banzaiRole,
      highestRole: highestRole as DefaultRoles,
      highestRoleFromBanzai,
      otherAccountsForService,
      otherServices,
      originalBanzaiRole: banzaiRole,
      overrideAdmin,
    };

    return result;
  }, []);

  return accountResult;
}
